import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { recordKillSwitchTelemetry } from "@/lib/purchaseKillSwitchTelemetry";
import { devError, devWarn } from "@/lib/devLog";

const CACHE_KEY = "zooi_purchase_killswitch";
const CACHE_TTL = 60_000; // 1 min
const LOG_PREFIX = "[purchase-killswitch]";

interface CachedFlag {
  paused: boolean;
  ts: number;
}

export type KillSwitchErrorCode =
  | "rpc_error"
  | "rpc_406"
  | "rpc_network"
  | "rpc_unexpected_type"
  | "update_failed";

export interface KillSwitchError {
  code: KillSwitchErrorCode;
  message: string;
  status?: number;
  cause?: unknown;
}

function toError(cause: unknown, fallback: string): KillSwitchError {
  if (cause && typeof cause === "object") {
    const c = cause as { message?: string; code?: string; status?: number; details?: string };
    const status = c.status;
    const msg = c.message ?? c.details ?? fallback;
    if (status === 406 || c.code === "PGRST116") {
      return {
        code: "rpc_406",
        message: `${msg} (HTTP 406 — RLS likely blocking direct table access; must use RPC)`,
        status: 406,
        cause,
      };
    }
    return { code: "rpc_error", message: msg, status, cause };
  }
  return { code: "rpc_error", message: fallback, cause };
}

function logErr(scope: string, err: KillSwitchError) {
  // eslint-disable-next-line no-console
  devError(`${LOG_PREFIX} ${scope}: ${err.code} — ${err.message}`, {
    status: err.status,
    cause: err.cause,
  });
}

function logWarn(scope: string, msg: string, extra?: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  devWarn(`${LOG_PREFIX} ${scope}: ${msg}`, extra ?? {});
}

function getCached(): boolean | null {
  try {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedFlag = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL) return null;
    return cached.paused;
  } catch (cause) {
    logWarn("getCached", "failed to read cache", { cause });
    return null;
  }
}

function setCache(paused: boolean) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ paused, ts: Date.now() }));
  } catch (cause) {
    logWarn("setCache", "failed to write cache", { cause });
  }
}

// Module-level in-flight promise so concurrent callers share one network request.
let inFlight: Promise<{ value: boolean; error: KillSwitchError | null }> | null = null;

async function fetchPausedFlag(): Promise<{ value: boolean; error: KillSwitchError | null }> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    try {
      const { data, error } = await supabase.rpc("get_purchase_tracking_paused" as never);
      const latency = Math.round(
        (typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt,
      );
      if (error) {
        const wrapped = toError(error, "RPC get_purchase_tracking_paused failed");
        logErr("fetchPausedFlag", wrapped);
        recordKillSwitchTelemetry({
          event_type: "rpc_fetch",
          cache_hit: false,
          latency_ms: latency,
          error_code: wrapped.code,
        });
        return { value: false, error: wrapped };
      }
      if (typeof data !== "boolean") {
        const wrapped: KillSwitchError = {
          code: "rpc_unexpected_type",
          message: `Expected boolean, got ${typeof data} (${JSON.stringify(data)})`,
        };
        logErr("fetchPausedFlag", wrapped);
        recordKillSwitchTelemetry({
          event_type: "rpc_fetch",
          cache_hit: false,
          latency_ms: latency,
          error_code: wrapped.code,
        });
        return { value: false, error: wrapped };
      }
      setCache(data);
      recordKillSwitchTelemetry({
        event_type: "rpc_fetch",
        paused_state: data,
        cache_hit: false,
        latency_ms: latency,
      });
      return { value: data, error: null };
    } catch (cause) {
      const wrapped: KillSwitchError = {
        code: "rpc_network",
        message: cause instanceof Error ? cause.message : "Network error calling RPC",
        cause,
      };
      logErr("fetchPausedFlag", wrapped);
      recordKillSwitchTelemetry({
        event_type: "rpc_fetch",
        cache_hit: false,
        error_code: wrapped.code,
      });
      return { value: false, error: wrapped }; // fail-open
    } finally {
      queueMicrotask(() => {
        inFlight = null;
      });
    }
  })();
  return inFlight;
}

/**
 * Fetch kill-switch flag (non-hook, for use in gtmDataLayer).
 * Returns boolean directly; logs errors to console via [purchase-killswitch] prefix.
 * Fail-open: returns false on any error so tracking is not silently disabled.
 */
export async function isPurchaseTrackingPaused(): Promise<boolean> {
  const cached = getCached();
  if (cached !== null) {
    recordKillSwitchTelemetry({ event_type: "rpc_fetch", paused_state: cached, cache_hit: true });
    return cached;
  }
  const { value } = await fetchPausedFlag();
  return value;
}

/** Variant that also exposes the error for callers that want to react. */
export async function isPurchaseTrackingPausedDetailed(): Promise<{
  paused: boolean;
  error: KillSwitchError | null;
}> {
  const cached = getCached();
  if (cached !== null) {
    recordKillSwitchTelemetry({ event_type: "rpc_fetch", paused_state: cached, cache_hit: true });
    return { paused: cached, error: null };
  }
  const { value, error } = await fetchPausedFlag();
  return { paused: value, error };
}

/** React hook for admin UI. Hydrates from cache to avoid flicker; dedups via shared in-flight. */
export function usePurchaseKillSwitch() {
  const initialCached = getCached();
  const [paused, setPaused] = useState<boolean>(initialCached ?? false);
  const [loading, setLoading] = useState<boolean>(initialCached === null);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState<KillSwitchError | null>(null);
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async (force = false) => {
    if (loadingRef.current) return;
    if (!force) {
      const cached = getCached();
      if (cached !== null) {
        if (mountedRef.current) {
          setPaused(cached);
          setLoading(false);
          setError(null);
        }
        return;
      }
    }
    loadingRef.current = true;
    if (mountedRef.current) setLoading(true);
    try {
      const { value, error: err } = await fetchPausedFlag();
      if (mountedRef.current) {
        setPaused(value);
        setError(err);
      }
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const toggle = useCallback(async () => {
    if (toggling) return;
    setToggling(true);
    setError(null);
    let next = false;
    setPaused((prev) => {
      next = !prev;
      return next;
    });
    try {
      const { error: updateErr } = await supabase
        .from("cos_system_flags" as never)
        .update({ purchase_tracking_paused: next } as never)
        .eq("id" as never, 1 as never);
      if (updateErr) {
        const wrapped = toError(updateErr, "Failed to update cos_system_flags");
        wrapped.code = "update_failed";
        logErr("toggle", wrapped);
        setPaused((prev) => !prev); // rollback
        if (mountedRef.current) setError(wrapped);
        return;
      }
      setCache(next);
      recordKillSwitchTelemetry({ event_type: "toggle", paused_state: next });
    } catch (cause) {
      const wrapped: KillSwitchError = {
        code: "update_failed",
        message: cause instanceof Error ? cause.message : "Update threw",
        cause,
      };
      logErr("toggle", wrapped);
      setPaused((prev) => !prev);
      if (mountedRef.current) setError(wrapped);
    } finally {
      if (mountedRef.current) setToggling(false);
    }
  }, [toggling]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(() => load(true), [load]);

  return { paused, loading, toggling, error, toggle, reload };
}
